import { TombstoneQuote } from '../../../types/tombstones';
import { sortByColumn, SortDirection, SortColumn } from '../../../utils/sort';

export { sortByColumn as sortQuotes };
export type { SortColumn, SortDirection };

/**
 * Format a number as Brazilian currency (BRL)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Get the public PDF URL for a tombstone quote using its token
 */
export function getPublicPdfUrl(pdfToken: string): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/v1$/, '') || 'http://localhost:3001';
  return `${baseUrl}/v1/tombstones/quotes/${pdfToken}/pdf`;
}

/**
 * Open WhatsApp share dialog with tombstone quote PDF link
 */
export function handleShareWhatsApp(quote: TombstoneQuote): void {
  if (!quote.pdfToken) return;

  const pdfUrl = getPublicPdfUrl(quote.pdfToken);
  const message = `Ola ${quote.customerName}! Segue o link do orcamento de tombstones ${quote.quoteNumber}: ${pdfUrl}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  window.open(whatsappUrl, '_blank');
}

/**
 * Get the status badge variant based on tombstone quote status
 */
export function getStatusBadgeVariant(status: string): 'pending' | 'approved' | 'rejected' | 'default' | 'secondary' {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'sent':
      return 'default';
    default:
      return 'secondary';
  }
}

/**
 * Get the translated status label
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    sent: 'Enviado',
  };
  return labels[status] || status;
}

/**
 * Calculate tombstone quote totals by status
 */
export function calculateQuoteTotals(quotes: TombstoneQuote[]): Record<'all' | 'pending' | 'approved' | 'rejected', number> {
  const totals = {
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  };

  quotes.forEach((quote) => {
    totals.all += quote.totalAmount;
    if (quote.status === 'pending') {
      totals.pending += quote.totalAmount;
    } else if (quote.status === 'approved') {
      totals.approved += quote.totalAmount;
    } else if (quote.status === 'rejected') {
      totals.rejected += quote.totalAmount;
    }
  });

  return totals;
}
